import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import Header from "../header/header";
import { useAnalyseLogs } from "./hooks/useAnalyseLogs/useAnalyseLogs";

const formatDuration = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;

  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${(ms / 1000).toFixed(1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
};

const calculateStats = (durations: number[]) => {
  if (durations.length === 0) return { min: 0, max: 0, avg: 0, count: 0 };
  const min = Math.min(...durations);
  const max = Math.max(...durations);
  const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
  return { min, max, avg, count: durations.length };
};

const getRunLabel = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getHalfHourBucket = (date: Date) => {
  const bucketMs = 30 * 60 * 1000;
  return Math.floor(date.getTime() / bucketMs) * bucketMs;
};

const getBucketLabel = (timestamp: number) => {
  return new Date(timestamp).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const PerformanceScreen = () => {
  const { analysisOS, analysisCCSV } = useAnalyseLogs();

  if (!analysisOS) {
    return (
      <Container maxWidth="xl">
        <Header back />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Typography>Loading performance data...</Typography>
        </Box>
      </Container>
    );
  }

  const ordersStats = calculateStats(analysisOS.ordersDuration);
  const syncStats = calculateStats(analysisOS.databaseSyncDuration);

  // Prepare data for charts
  const ordersData = analysisOS.ordersDuration.map((duration, index) => ({
    x: index + 1,
    y: duration,
  }));

  const syncData = analysisOS.databaseSyncDuration.map((duration, index) => ({
    x: index + 1,
    y: duration,
  }));

  const orderCycleData = analysisOS.orderCycleDurations.map(
    (duration, index) => ({
      x: index + 1,
      y: duration,
    }),
  );

  const syncCycleData = analysisOS.syncCycleDurations.map(
    (duration, index) => ({
      x: index + 1,
      y: duration,
    }),
  );

  const ccsvAlgorithms = Array.from(
    new Set(
      analysisCCSV?.runs.flatMap((run) =>
        run.algorithmDurations.map((item) => item.algorithm),
      ) ?? [],
    ),
  );

  const bucketedRunsFromRaw = (analysisCCSV?.runs ?? []).reduce((map, run) => {
    const bucketKey = String(getHalfHourBucket(run.runAt));
    if (!map.has(bucketKey)) {
      map.set(bucketKey, new Map<string, number>());
    }
    const bucket = map.get(bucketKey)!;
    run.algorithmDurations.forEach((duration) => {
      bucket.set(
        duration.algorithm,
        Math.max(bucket.get(duration.algorithm) ?? 0, duration.durationMs),
      );
    });
    return map;
  }, new Map<string, Map<string, number>>());

  const fallbackBucketedRuns = Array.from(bucketedRunsFromRaw.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([bucketKey, bucket]) => ({
      bucketAt: new Date(Number(bucketKey)),
      bucketLabel: getBucketLabel(Number(bucketKey)),
      algorithmDurations: Array.from(bucket.entries()).map(
        ([algorithm, durationMs]) => ({
          algorithm,
          durationMs,
          durationText: "",
        }),
      ),
    }));

  const ccsvBucketedRuns =
    analysisCCSV?.bucketedRuns && analysisCCSV.bucketedRuns.length > 0
      ? analysisCCSV.bucketedRuns
      : fallbackBucketedRuns;

  const hasValidBucketDates = ccsvBucketedRuns.every(
    (run) =>
      run.bucketAt instanceof Date && !Number.isNaN(run.bucketAt.getTime()),
  );

  const ccsvDataset = ccsvBucketedRuns.map((run) => {
    const row: Record<string, number | string> = {
      x: hasValidBucketDates ? run.bucketAt!.getTime() : run.bucketLabel,
    };
    ccsvAlgorithms.forEach((algorithm) => {
      const entry = run.algorithmDurations.find(
        (item) => item.algorithm === algorithm,
      );
      row[algorithm] = entry?.durationMs ?? 0;
    });
    return row;
  });
  console.log("🚀 ~ PerformanceScreen ~ ccsvDataset:", ccsvDataset);
  console.log("DEBUG", {
    rawRunsCount: analysisCCSV?.runs?.length,
    firstRunAlgorithms: analysisCCSV?.runs?.[0]?.algorithmDurations,
    firstRunAt: analysisCCSV?.runs?.[0]?.runAt,
    firstRunAtText: analysisCCSV?.runs?.[0]?.runAtText,
    bucketedRunsCount: analysisCCSV?.bucketedRuns?.length,
    firstBucketAlgorithms: analysisCCSV?.bucketedRuns?.[0]?.algorithmDurations,
    ccsvAlgorithmsCount: ccsvAlgorithms.length,
  });

  const ccsvSeries = ccsvAlgorithms.map((algorithm) => ({
    dataKey: algorithm,
    label: algorithm,
    area: true,
    stack: "algorithms",
    // curve: "monotoneX",
    showMark: false,
    valueFormatter: (value: number | null) => formatDuration(value ?? 0),
  }));
  console.log("🚀 ~ PerformanceScreen ~ ccsvSeries:", ccsvSeries);

  return (
    <Container maxWidth="xl">
      <Header back />
      <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
        Performance Analysis
      </Typography>

      <Grid container spacing={3}>
        {/* Order Creator Stats */}
        {/* <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Creator Performance
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Operations: {ordersStats.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average: {formatDuration(ordersStats.avg)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Min: {formatDuration(ordersStats.min)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Max: {formatDuration(ordersStats.max)}
                </Typography>
              </Box>
              {ordersData.length > 0 && (
                <LineChart
                  dataset={ordersData}
                  xAxis={[{ dataKey: "x", label: "Operation #" }]}
                  series={[
                    {
                      dataKey: "y",
                      label: "Duration",
                      valueFormatter: (value) => formatDuration(value ?? 0),
                    },
                  ]}
                  height={600}
                  margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid> */}

        {/* Database Sync Stats */}
        {/* <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Sync Performance
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Operations: {syncStats.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average: {formatDuration(syncStats.avg)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Min: {formatDuration(syncStats.min)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Max: {formatDuration(syncStats.max)}
                </Typography>
              </Box>
              {syncData.length > 0 && (
                <LineChart
                  dataset={syncData}
                  xAxis={[{ dataKey: "x", label: "Operation #" }]}
                  series={[
                    {
                      dataKey: "y",
                      label: "Duration",
                      valueFormatter: (value) => formatDuration(value ?? 0),
                    },
                  ]}
                  height={600}
                  margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid> */}

        {/* Cycle Time Charts */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Creator Cycle Time
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total cycles: {analysisOS.orderCycleDurations.length}
              </Typography>
              {orderCycleData.length > 0 && (
                <LineChart
                  dataset={orderCycleData}
                  xAxis={[{ dataKey: "x", label: "Cycle #" }]}
                  series={[
                    {
                      dataKey: "y",
                      label: "Cycle Duration",
                      valueFormatter: (value) => formatDuration(value ?? 0),
                      showMark: false,
                    },
                  ]}
                  height={240}
                  margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Sync Cycle Time
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total cycles: {analysisOS.syncCycleDurations.length}
              </Typography>
              {syncCycleData.length > 0 && (
                <LineChart
                  dataset={syncCycleData}
                  xAxis={[{ dataKey: "x", label: "Cycle #" }]}
                  series={[
                    {
                      dataKey: "y",
                      label: "Cycle Duration",
                      valueFormatter: (value) => formatDuration(value ?? 0),
                      showMark: false,
                    },
                  ]}
                  height={240}
                  margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {analysisCCSV && ccsvDataset.length > 0 && (
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  CreateCSV Algorithm Run Times
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Timepoints: {ccsvBucketedRuns.length} | Algorithms:{" "}
                  {ccsvAlgorithms.length}
                </Typography>
                <LineChart
                  dataset={ccsvDataset}
                  xAxis={[
                    {
                      dataKey: "x",
                      scaleType: hasValidBucketDates ? "time" : "point",
                      label: "Run time",
                      valueFormatter: (value: unknown) =>
                        hasValidBucketDates
                          ? getRunLabel(value as number)
                          : String(value ?? ""),
                    },
                  ]}
                  yAxis={[{ valueFormatter: (value: unknown) => formatDuration((value as number) ?? 0) }]}
                  series={ccsvSeries}
                  height={360}
                  margin={{ left: 70, right: 20, top: 20, bottom: 40 }}
                  sx={{ "& svg": { overflow: "visible" } }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Combined Performance Overview */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle1">Order Creator</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Operations: {ordersStats.count} | Avg:{" "}
                    {formatDuration(ordersStats.avg)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cycle Time: {formatDuration(analysisOS.orderCycleDuration)}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle1">Database Sync</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Operations: {syncStats.count} | Avg:{" "}
                    {formatDuration(syncStats.avg)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cycle Time: {formatDuration(analysisOS.syncCycleDuration)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};
